if (!customElements.get('collection-list')) {
  class CollectionList extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback() {
      this.selectors = {
        sliderWrapper: '.collection-list__items',
        pagination: '.swiper-pagination',
        nextEl: '.swiper-btn-next',
        prevEl: '.swiper-btn-prev',
      };
      this.classes = {
        grid: 'f-grid',
        swiper: 'swiper',
        swiperWrapper: 'swiper-wrapper',
        scrollable: 'collection-list--scrollable',
        scrollableWrapper: 'collection-list__items--scrollable',
      };

      this.sectionId = this.dataset.sectionId;
      this.section = this.closest(`.section-${this.sectionId}`);
      this.sliderWrapper = this.querySelector(this.selectors.sliderWrapper);

      this.enableSlider = this.dataset.enableSlider === 'true';
      this.enableScrollable = this.dataset.enableScrollable === 'true';
      this.items = parseInt(this.dataset.items);
      this.tabletItems = parseInt(this.dataset.tabletItems);

      this.sliderInstance = false;

      // If scrollable mode is enabled, use scrollable instead of slider
      if (this.enableScrollable) {
        this.initScrollable();
        return;
      }

      if (!this.enableSlider) return;

      this.init();
      document.addEventListener('matchMobile', () => {
        this.init();
      });
      document.addEventListener('unmatchMobile', () => {
        this.init();
      });
    }

    initScrollable() {
      // Add scrollable classes
      this.classList.add(this.classes.scrollable);
      this.sliderWrapper.classList.add(this.classes.scrollableWrapper);
      
      // Remove grid classes that might interfere
      this.sliderWrapper.classList.remove(this.classes.grid);
      // Remove responsive grid classes
      const gridClasses = Array.from(this.sliderWrapper.classList).filter(cls => 
        cls.includes('f-grid') || cls.includes('-cols')
      );
      gridClasses.forEach(cls => this.sliderWrapper.classList.remove(cls));
      
      // Setup scroll controls
      const nextBtn = this.section.querySelector(this.selectors.nextEl);
      const prevBtn = this.section.querySelector(this.selectors.prevEl);

      if (nextBtn && prevBtn) {
        this.setupScrollControls(nextBtn, prevBtn);
      }

      // Add scroll snap for smooth scrolling
      this.sliderWrapper.style.scrollBehavior = 'smooth';
      
      // Enable keyboard navigation
      this.enableKeyboardNavigation();
      
      // Update button states on scroll
      this.updateScrollButtons();
      this.sliderWrapper.addEventListener('scroll', () => {
        this.updateScrollButtons();
      });
    }

    enableKeyboardNavigation() {
      // Make the container focusable
      this.sliderWrapper.setAttribute('tabindex', '0');
      
      this.sliderWrapper.addEventListener('keydown', (e) => {
        const scrollAmount = this.calculateScrollAmount();
        
        switch(e.key) {
          case 'ArrowLeft':
            e.preventDefault();
            this.sliderWrapper.scrollBy({ left: -scrollAmount, behavior: 'smooth' });
            break;
          case 'ArrowRight':
            e.preventDefault();
            this.sliderWrapper.scrollBy({ left: scrollAmount, behavior: 'smooth' });
            break;
          case 'Home':
            e.preventDefault();
            this.sliderWrapper.scrollTo({ left: 0, behavior: 'smooth' });
            break;
          case 'End':
            e.preventDefault();
            this.sliderWrapper.scrollTo({ left: this.sliderWrapper.scrollWidth, behavior: 'smooth' });
            break;
        }
      });
    }

    setupScrollControls(nextBtn, prevBtn) {
      nextBtn.addEventListener('click', () => {
        const scrollAmount = this.calculateScrollAmount();
        this.sliderWrapper.scrollBy({ left: scrollAmount, behavior: 'smooth' });
      });

      prevBtn.addEventListener('click', () => {
        const scrollAmount = this.calculateScrollAmount();
        this.sliderWrapper.scrollBy({ left: -scrollAmount, behavior: 'smooth' });
      });
    }

    calculateScrollAmount() {
      // Calculate scroll amount based on card width + gap
      const firstCard = this.sliderWrapper.querySelector('.f-column');
      if (!firstCard) return 300; // fallback value
      
      const cardWidth = firstCard.offsetWidth;
      const computedStyle = getComputedStyle(this.sliderWrapper);
      const gap = parseInt(computedStyle.columnGap) || 0;
      
      // Scroll by one card width + gap
      return cardWidth + gap;
    }

    updateScrollButtons() {
      const nextBtn = this.section.querySelector(this.selectors.nextEl);
      const prevBtn = this.section.querySelector(this.selectors.prevEl);

      if (!nextBtn || !prevBtn) return;

      const { scrollLeft, scrollWidth, clientWidth } = this.sliderWrapper;
      
      // Disable prev button at start
      if (scrollLeft <= 0) {
        prevBtn.disabled = true;
        prevBtn.style.opacity = '0.3';
      } else {
        prevBtn.disabled = false;
        prevBtn.style.opacity = '1';
      }

      // Disable next button at end
      if (scrollLeft + clientWidth >= scrollWidth - 1) {
        nextBtn.disabled = true;
        nextBtn.style.opacity = '0.3';
      } else {
        nextBtn.disabled = false;
        nextBtn.style.opacity = '1';
      }
    }

    init() {
      if (FoxTheme.config.mqlMobile) {
        this.destroySlider();
      } else {
        this.initSlider();
      }
    }

    initSlider() {
      const columnGap = FoxTheme.utils.getGridColumnGap(this.sliderWrapper);

      const sliderOptions = {
        slidesPerView: this.tabletItems > 3 ? 3 : parseInt(this.tabletItems),
        spaceBetween: columnGap.tablet,
        navigation: {
          nextEl: this.section.querySelector(this.selectors.nextEl),
          prevEl: this.section.querySelector(this.selectors.prevEl),
        },
        pagination: false,
        breakpoints: {
          1024: {
            slidesPerView: parseInt(this.items),
            spaceBetween: columnGap.tabletLarge
          },
          1280: {
            slidesPerView: parseInt(this.items),
            spaceBetween: columnGap.desktop
          },
        },
        loop: true,
        threshold: 2,
      };

      if (typeof this.sliderInstance !== 'object') {
        this.classList.add(this.classes.swiper);
        this.sliderWrapper.classList.remove(this.classes.grid);
        this.sliderWrapper.classList.add(this.classes.swiperWrapper);
        this.sliderInstance = new window.FoxTheme.Carousel(this, sliderOptions);
        this.sliderInstance.init();

        const focusableElements = FoxTheme.a11y.getFocusableElements(this);

        focusableElements.forEach((element) => {
          element.addEventListener('focusin', () => {
            const slide = element.closest('.swiper-slide');
            this.sliderInstance.slider.slideTo(this.sliderInstance.slider.slides.indexOf(slide));
          });
        });
      }

      if (Shopify.designMode && typeof this.sliderInstance === 'object') {
        document.addEventListener('shopify:block:select', (e) => {
          if (e.detail.sectionId != this.sectionId) return;
          let { target } = e;
          const index = Number(target.dataset.index);

          this.sliderInstance.slider.slideToLoop(index);
        });
      }
    }

    destroySlider() {
      this.classList.remove(this.classes.swiper);
      this.sliderWrapper.classList.remove(this.classes.swiperWrapper);
      this.sliderWrapper.classList.add(this.classes.grid);
      if (typeof this.sliderInstance === 'object') {
        this.sliderInstance.slider.destroy();
        this.sliderInstance = false;
      }
    }
  }
  customElements.define('collection-list', CollectionList);
}